import { getRichTextEntityLinks } from '@contentful/rich-text-links'
import {
  Asset as ContentfulAsset,
  createClient,
  Entry as ContentfulEntry,
} from 'contentful'
import { GetStaticPropsContext } from 'next'
import { useRouter } from 'next/router'
import React from 'react'

import SEO from '../../components/shared/SEO'
import NewsTemplate, {
  Asset,
  Entry,
} from '../../components/templates/NewsTemplate'
import {
  NewsDocument,
  NewsFieldsFragment,
  NewsQuery,
} from '../../generated/graphql'
import { initializeApollo } from '../../utils/ApolloClient'

type Props = {
  news: NewsFieldsFragment
  entries: [Entry]
  assets: [Asset]
}

const NewsPage: React.FC<Props> = ({ news, entries, assets }) => {
  const router = useRouter()
  if (router.isFallback) return null

  return (
    <>
      <SEO
        title={`${news.title} | お知らせ | RYDE株式会社`}
        openGraph={{
          images: [
            {
              url: 'https://ryde-inc.jp/og_image.png',
            },
          ],
          type: 'website',
          url: 'https://ryde-inc.jp/',
          site_name:
            'RYDE株式会社 | 二次交通をわかりやすく、使いやすく、そして持続可能に',
          locale: 'ja_JP',
        }}
      />
      <NewsTemplate news={news} entries={entries} assets={assets} />
    </>
  )
}

const client = createClient({
  space: process.env.NEXT_PUBLIC_CONTENTFUL_SPACE_ID!,
  accessToken: process.env.NEXT_PUBLIC_CONTENTFUL_ACCESS_TOKEN!,
})

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: true,
  }
}

export async function getStaticProps(context: GetStaticPropsContext) {
  const apolloClient = initializeApollo()
  const { data, error } = await apolloClient.query<NewsQuery>({
    query: NewsDocument,
    variables: { id: context.params?.newsId },
  })

  if (error) {
    throw error
  }
  if (!data.news) {
    return { notFound: true, revalidate: 60 }
  }

  const entries: Array<Entry> = []
  const assets: Array<Asset> = []

  const richTextLinks = getRichTextEntityLinks(data.news.content?.json)

  // Entryの一括取得
  // eslint-disable-next-line no-restricted-syntax
  for await (const entry of richTextLinks.Entry) {
    await client.getEntry(entry.id).then((result: ContentfulEntry<any>) => {
      entries.push({
        id: entry.id,
        title: result.fields.title ? result.fields.title.toString() : '',
      })
    })
  }

  // Assetの一括取得
  // eslint-disable-next-line no-restricted-syntax
  for await (const asset of richTextLinks.Asset) {
    await client.getAsset(asset.id).then((result: ContentfulAsset) => {
      assets.push({
        id: asset.id,
        title: result.fields.title ? result.fields.title.toString() : '',
        url:
          result.fields.file && result.fields.file.url
            ? `https:${result.fields.file.url.toString()}?h=300`
            : '',
      })
    })
  }

  return {
    props: {
      news: data.news,
      entries,
      assets,
    } as Props,
    revalidate: 60,
  }
}

export default NewsPage
